import type { Answers, Condition, IChecklistItem, IProgram } from './types.js';

/** Someone on an application. */
export interface IPerson {
  key: string;
  role: string;
  name: string;
  /** Age today, when known (for items only people of a certain age need). */
  age?: number;
}

/** One line of a file's checklist, before it's saved. */
export interface IChecklistLine {
  itemId: string;
  /** Empty for items that belong to the file rather than one person. */
  personKey: string;
  /** "Passport (Khalil, Omar)". */
  label: string;
  section: string;
  form?: string;
  hint?: string;
  translationIfNotEnFr?: boolean;
}

export function holds(c: Condition | undefined, answers: Answers): boolean {
  if (!c) {
    return true;
  }
  if ('q' in c) {
    const a = answers[c.q];
    return a !== undefined && String(a) === String(c.is);
  }
  if ('all' in c) {
    return c.all.every(x => holds(x, answers));
  }
  if ('any' in c) {
    return c.any.some(x => holds(x, answers));
  }
  return !holds(c.not, answers);
}

function lineFor(item: IChecklistItem, person: IPerson | undefined): IChecklistLine {
  return {
    itemId: item.id,
    personKey: person?.key ?? '',
    label: person ? `${item.label} (${person.name})` : item.label,
    section: item.section,
    form: item.form,
    hint: item.hint,
    translationIfNotEnFr: item.translationIfNotEnFr
  };
}

/**
 * The checklist for one application: every item that applies given the answers, once for the
 * file or once per person in the item's roles (only people old enough, when the item says so;
 * a person of unknown age gets it, to be safe). In the program's section order.
 */
export function buildChecklist(program: IProgram, answers: Answers, people: IPerson[]): IChecklistLine[] {
  const lines: IChecklistLine[] = [];
  for (const item of program.items) {
    if (!holds(item.when, answers)) {
      continue;
    }
    if (item.forRoles === 'application') {
      lines.push(lineFor(item, undefined));
      continue;
    }
    const roles: string[] = item.forRoles;
    for (const p of people.filter(x => roles.indexOf(x.role) !== -1)) {
      if (item.minAge !== undefined && p.age !== undefined && p.age < item.minAge) {
        continue;
      }
      lines.push(lineFor(item, p));
    }
  }
  const order = (s: string): number => {
    const i: number = program.sections.indexOf(s);
    return i === -1 ? program.sections.length : i;
  };
  return lines
    .map((l, i) => ({ l, i }))
    .sort((a, b) => order(a.l.section) - order(b.l.section) || a.i - b.i)
    .map(x => x.l);
}

/** People who are missing or too many for the program's roles, in plain words; empty when fine. */
export function peopleProblems(program: IProgram, people: IPerson[]): string[] {
  const out: string[] = [];
  for (const r of program.roles) {
    const n: number = people.filter(p => p.role === r.id).length;
    if (n < r.min) {
      out.push(r.min === 1 ? `Add the ${r.label.toLowerCase()}.` : `Add at least ${r.min} people as ${r.label.toLowerCase()}.`);
    }
    if (n > r.max) {
      out.push(`There can be at most ${r.max} ${r.label.toLowerCase()}${r.max === 1 ? '' : 's'}.`);
    }
  }
  return out;
}

/**
 * What changes when a file's checklist is built again (new answers, new people, or a newer
 * version of the program): lines to add and lines that no longer apply. Staff see both and
 * decide; nothing is applied on its own.
 */
export function diffChecklist(current: { itemId: string; personKey: string }[], wanted: IChecklistLine[]): { add: IChecklistLine[]; gone: { itemId: string; personKey: string }[] } {
  const key = (x: { itemId: string; personKey: string }): string => `${x.itemId}|${x.personKey}`;
  const have: { [k: string]: boolean } = {};
  for (const c of current) {
    have[key(c)] = true;
  }
  const want: { [k: string]: boolean } = {};
  for (const w of wanted) {
    want[key(w)] = true;
  }
  return {
    add: wanted.filter(w => !have[key(w)]),
    gone: current.filter(c => !want[key(c)])
  };
}
